import { Flex, Text, SettingsIcon, Button, segmentClassName } from "@fluentui/react-northstar";
import { getThemeMainColor } from "msTeamsUI/chartThemes/chartThemes";
import React, { useMemo, useState } from "react";
import { constants } from "shared/constants";
import { ReportBox } from "view360Card/components/companyReportGroupView/reportBox/ReportBox";
import { OrderableSementsFilter } from "../filters";
import { DailySalesCompany } from "../models/daily-sales";
import { Money } from "ecom-teams-apps-core";
import { LabeledReportItem } from "view360Card/components/companyReportGroupView/labeledReportItem/LabeledReportItem";
import { formatNumber } from "utils/formattingUtils";
import { DailySegment } from "./DailySegment";
import { Selectable } from "../models/abstractions/Selectable";
import { Segment } from "../models";

export interface DailyCompanyProps {
    report: DailySalesCompany;
    segmentFilterList: Segment[];
    isMobileView: boolean;
    onSelectedFilterChanged: (companyCode: string, segmentList: Segment[]) => void;
}

export const DailyCompany: React.FC<DailyCompanyProps> = (props) => {
    const [filterDisabled, setFilterDisabled] = useState(false);

    const highlightColor = useMemo(() => {
        if (!props.report.colorScheme) {
            return undefined;
        }

        return getThemeMainColor(props.report.colorScheme);
    }, [props.report.colorScheme]);

    const premiumSeries = useMemo(() => {
        return props.report.seriesList.find((s) => s.code === "PREMIUM");
    }, []);

    const countSeries = useMemo(() => {
        return props.report.seriesList.find((s) => s.code === "COUNT");
    }, []);

    const selectedSegmentsValues = useMemo(() => {
        return props.report.segmentList.filter(
            (s) =>
                props.segmentFilterList.findIndex(
                    (segmentFilter) => segmentFilter.selected && segmentFilter.code === s.segmentLabel?.code
                ) > -1
        );
    }, [props.segmentFilterList]);

    const premiumValue = useMemo(() => {
        const segments = selectedSegmentsValues;

        return segments
            .map((s) => s.premium)
            .reduce((a, current) => {
                return (a ?? 0) + (current ?? 0);
            }, undefined);
    }, [props.segmentFilterList]);

    const countValue = useMemo(() => {
        const segments = selectedSegmentsValues;

        return segments
            .map((s) => s.count)
            .reduce((a, current) => {
                return (a ?? 0) + (current ?? 0);
            }, undefined);
    }, [props.segmentFilterList]);

    const content = useMemo(() => {
        return (
            <Flex gap="gap.small" column fill>
                <Flex>
                    <LabeledReportItem
                        label={premiumSeries?.name ?? constants.noValue}
                        tooltip={premiumSeries?.tooltip}
                        content={
                            <Text weight="bold" size="larger">
                                {premiumValue !== undefined
                                    ? new Money(premiumValue, constants.fractionDigits).toStringWithCurrency()
                                    : constants.noValue}
                            </Text>
                        }
                    />
                    <LabeledReportItem
                        label={countSeries?.name ?? constants.noValue}
                        tooltip={countSeries?.tooltip}
                        content={
                            <Text weight="bold" size="larger">
                                {countValue !== undefined ? formatNumber(countValue) : constants.noValue}
                            </Text>
                        }
                    />
                </Flex>
                {/* <Flex gap="gap.small" wrap>
                    {props.report.segmentList
                        .filter(
                            (s) =>
                                props.segmentFilterList.findIndex(
                                    (segment) => segment.code === s.segmentLabel?.code && segment.selected
                                ) > -1
                        )
                        .map((s) => {
                            return <DailySegment key={s.segmentLabel?.code ?? ""} segment={s} />;
                        })}
                </Flex> */}
            </Flex>
        );
    }, [props.report, props.segmentFilterList]);

    const filters = useMemo(() => {
        const trigger = <Button icon={<SettingsIcon styles={{ color: constants.actionIconColor }} />} text iconOnly />;
        return (
            <OrderableSementsFilter
                disabled={filterDisabled}
                trigger={trigger}
                multiple
                items={props.segmentFilterList}
                onSelectedItemsChanged={function (items: Selectable[]): void {
                    setFilterDisabled(true);
                    props.onSelectedFilterChanged(props.report.code ?? "", items as Segment[]);
                    setFilterDisabled(false);
                }}
            />
        );
    }, [props.report, props.segmentFilterList]);

    return (
        <Flex
            fill
            style={{ borderLeft: highlightColor ? `4px solid ${highlightColor}` : undefined, paddingLeft: "10px" }}
        >
            <ReportBox noGap title={props.report.name ?? ""} content={content} headerAddOn={filters} />
        </Flex>
    );
};
